// src/components/ReportList.js
import React, { useState, useEffect } from "react";
import axios from "../utils/axiosConfig"; // 引入已設定 CSRF 的 axios
import API_URL from "../config";
import { useTranslation } from "react-i18next"; // 引入國際化函數 (i18n: Internationalization)
import { Link, useNavigate } from "react-router-dom";
import { useLoading } from '../contexts/LoadingContext';

const ReportList = () => {
  const { t } = useTranslation(); // 使用 i18n 翻譯函數
  const navigate = useNavigate();
  const { showLoading, hideLoading } = useLoading();
  const [reports, setReports] = useState([]); // 儲存平坦化後的報告資料
  const [filteredReports, setFilteredReports] = useState([]); // 儲存經過搜尋、篩選與排序後的資料
  const [searchQuery, setSearchQuery] = useState(""); // 搜尋關鍵字
  const [timeFilter, setTimeFilter] = useState("all"); // 時間篩選器： "all", "24h", "7d", "30d"
  const [sortField, setSortField] = useState("date"); // 排序欄位
  const [sortOrder, setSortOrder] = useState("desc"); // 排序順序： "asc" 或 "desc"
  const [currentPage, setCurrentPage] = useState(1); // 當前頁碼

  useEffect(() => {
    const fetchReports = async () => {
      showLoading();
      try {
        // 使用普通報告 API 端點，因為後端暫無專門的公開報告端點
        const response = await axios.get(`${API_URL}/api/reports`, { withCredentials: true });
        
        if (response.data) {
          // 平坦化資料處理...
          const flattenedReports = [];
          
          // 只處理公開報告
          const publicReports = response.data.filter(report => report.reportType === 'public');
          
          publicReports.forEach((report) => {
            // 報告內容處理邏輯保持不變
            if (report.scammerAddresses && report.scammerAddresses.length > 0) {
              report.scammerAddresses.forEach((address, index) => {
                flattenedReports.push({
                  ...report,
                  id: `${report._id}-${index}`,
                  originalId: report._id,
                  reportId: report._id,
                  scammerAddress: address,
                  // 正確提取 address 中的錢包地址 (依據後端的結構)
                  scammerWalletAddress: address.scammerWalletAddress || '',
                  victimWalletAddress: address.victimWalletAddress || '',
                  amount: address.amount || 0,
                  currency: address.currency || '',
                  date: address.date || report.createdAt,
                  chain: address.chain || '',
                  transactionID: address.transactionID || '',
                });
              });
            } else {
              flattenedReports.push({
                ...report,
                id: report._id,
                originalId: report._id,
                reportId: report._id,
                scammerAddress: "",
                scammerWalletAddress: '',
                victimWalletAddress: '',
                amount: 0,
                currency: '',
                date: report.createdAt,
              });
            }
          });
          
          setReports(flattenedReports);
          setFilteredReports(flattenedReports);
        }
      } catch (error) {
        console.error('獲取報告列表時發生錯誤：', error);
        if (error.response && error.response.status === 401) {
          navigate('/login');
        }
      } finally {
        hideLoading();
      }
    };
    
    fetchReports();
  }, [navigate]);

  // 每當 reports、searchQuery、timeFilter、sortField 或 sortOrder 改變時，重新篩選及排序資料
  useEffect(() => {
    let tempReports = [...reports];

    // 搜尋過濾：根據 scammerWalletAddress、incidentType 或 victimWalletAddress 模糊比對
    if (searchQuery) {
        const query = searchQuery.toLowerCase();
        tempReports = tempReports.filter((item) => {
          // 取得原始的英文 incidentType（如果有的話）
          const incidentTypeEnglish = item.incidentType ? item.incidentType.toLowerCase() : "";
          // 取得翻譯後的 incidentType（中文），並轉為小寫
          const incidentTypeTranslated = item.incidentType ? t(item.incidentType).toLowerCase() : "";
          // 其他欄位的比對
          const scammerAddress = item.scammerWalletAddress ? item.scammerWalletAddress.toLowerCase() : "";
          const victimAddress = item.victimWalletAddress ? item.victimWalletAddress.toLowerCase() : "";
      
          return (
            scammerAddress.includes(query) ||
            victimAddress.includes(query) ||
            incidentTypeEnglish.includes(query) ||
            incidentTypeTranslated.includes(query)
          );
        });
      }
      

    // 時間篩選：根據所選時間範圍過濾資料
    if (timeFilter !== "all") {
      const now = new Date();
      let days = 0;
      if (timeFilter === "1D") days = 1;
      else if (timeFilter === "7D") days = 7;
      else if (timeFilter === "30D") days = 30;
      tempReports = tempReports.filter((item) => {
        // 確保日期有效
        const itemDate = item.date ? new Date(item.date) : null;
        if (!itemDate || isNaN(itemDate)) return false;
        
        const diffTime = Math.abs(now - itemDate);
        const diffDays = diffTime / (1000 * 60 * 60 * 24);
        return diffDays <= days;
      });
    }

    // 排序：依 sortField 與 sortOrder 排序
    if (sortField) {
      tempReports.sort((a, b) => {
        let aValue, bValue;
        if (sortField === "date") {
          // 確保日期有效
          aValue = a.date ? new Date(a.date) : new Date(0);
          bValue = b.date ? new Date(b.date) : new Date(0);
          
          // 如果日期無效，使用 0 作為比較值
          if (isNaN(aValue)) aValue = new Date(0);
          if (isNaN(bValue)) bValue = new Date(0);
        } else if (sortField === "amount") {
          aValue = parseFloat(a.amount) || 0;
          bValue = parseFloat(b.amount) || 0;
        }
        if (aValue < bValue) return sortOrder === "asc" ? -1 : 1;
        if (aValue > bValue) return sortOrder === "asc" ? 1 : -1;
        return 0;
      });
    }

    setFilteredReports(tempReports);
  }, [reports, searchQuery, timeFilter, sortField, sortOrder, t]);

  // 計算統計數據：總通報數、總損失估計、7日內通報數、7日內損失估計
  const totalAlerts = reports.length;
  const totalEstimatedLoss = reports.reduce(
    (sum, item) => sum + (parseFloat(item.amount) || 0),
    0
  ).toFixed(1);
  const now = new Date();
  const alertsIn7D = reports.filter((item) => {
    // 確保日期有效
    const itemDate = item.date ? new Date(item.date) : null;
    if (!itemDate || isNaN(itemDate)) return false;
    
    const diffTime = Math.abs(now - itemDate);
    const diffDays = diffTime / (1000 * 60 * 60 * 24);
    return diffDays <= 7;
  }).length;
  const lossIn7D = reports
    .filter((item) => {
      // 確保日期有效
      const itemDate = item.date ? new Date(item.date) : null;
      if (!itemDate || isNaN(itemDate)) return false;
      
      const diffTime = Math.abs(now - itemDate);
      const diffDays = diffTime / (1000 * 60 * 60 * 24);
      return diffDays <= 7;
    })
    .reduce((sum, item) => sum + (parseFloat(item.amount) || 0), 0)
    .toFixed(1);

  // 處理排序標題點選事件
  const handleSort = (field) => {
    if (sortField === field) {
      // 若已經為該欄位則切換排序順序
      setSortOrder(sortOrder === "asc" ? "desc" : "asc");
    } else {
      setSortField(field);
      setSortOrder("asc"); // 初始設定為升序
    }
  };

  return (
    <div className="min-h-[300px]">
{/* 統計數據卡片區塊 */}
<div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
  {/* 卡片1 - 總警報數 */}
  <div className="bg-gradient-to-br from-purple-50 to-indigo-100 dark:from-indigo-800/80 dark:to-purple-700/80 rounded-xl shadow-lg overflow-hidden border border-purple-200 dark:border-indigo-500/30 transition-all duration-300 hover:shadow-purple-400/10 hover:scale-105">
    <div className="p-5">
      <div className="flex items-center justify-between">
        <div className="text-purple-600 dark:text-purple-300 text-sm font-medium tracking-wider uppercase">{t("totalAlerts")}</div>
        <div className="bg-purple-100/70 dark:bg-purple-500/10 p-2 rounded-full">
          <svg className="h-5 w-5 text-purple-500 dark:text-purple-300" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" />
            <path d="M13.73 21a2 2 0 0 1-3.46 0" />
          </svg>
        </div>
      </div>
      <div className="mt-4 flex items-baseline">
        <div className="text-3xl font-extrabold text-purple-700 dark:text-purple-100">{totalAlerts}</div>
        <div className="ml-2 text-xs font-semibold text-purple-500 dark:text-purple-300">alerts</div>
      </div>
    </div>
    <div className="h-1 w-full bg-gradient-to-r from-purple-200 to-indigo-300 dark:from-purple-500/60 dark:to-indigo-400/60"></div>
  </div>

  {/* 卡片2 - 總估計損失 */}
  <div className="bg-gradient-to-br from-indigo-50 to-purple-100 dark:from-purple-800/80 dark:to-indigo-700/80 rounded-xl shadow-lg overflow-hidden border border-indigo-200 dark:border-purple-500/30 transition-all duration-300 hover:shadow-indigo-400/10 hover:scale-105">
    <div className="p-5">
      <div className="flex items-center justify-between">
        <div className="text-indigo-600 dark:text-indigo-300 text-sm font-medium tracking-wider uppercase">{t("totalEstimatedLoss")}</div>
        <div className="bg-indigo-100/70 dark:bg-indigo-500/10 p-2 rounded-full">
          <svg className="h-5 w-5 text-indigo-500 dark:text-indigo-300" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="12" cy="12" r="10" />
            <line x1="12" y1="8" x2="12" y2="12" />
            <line x1="12" y1="16" x2="12.01" y2="16" />
          </svg>
        </div>
      </div>
      <div className="mt-4 flex items-baseline">
        <div className="text-3xl font-extrabold text-indigo-700 dark:text-indigo-100">{totalEstimatedLoss}</div>
        <div className="ml-2 text-xs font-semibold text-indigo-500 dark:text-indigo-300">USD</div>
      </div>
    </div>
    <div className="h-1 w-full bg-gradient-to-r from-indigo-200 to-purple-300 dark:from-indigo-500/60 dark:to-purple-400/60"></div>
  </div>

  {/* 卡片3 - 7天內警報 */}
  <div className="bg-gradient-to-br from-violet-50 to-purple-100 dark:from-violet-800/80 dark:to-purple-700/80 rounded-xl shadow-lg overflow-hidden border border-violet-200 dark:border-violet-500/30 transition-all duration-300 hover:shadow-violet-400/10 hover:scale-105">
    <div className="p-5">
      <div className="flex items-center justify-between">
        <div className="text-violet-600 dark:text-violet-300 text-sm font-medium tracking-wider uppercase">{t("alertsIn7D")}</div>
        <div className="bg-violet-100/70 dark:bg-violet-500/10 p-2 rounded-full">
          <svg className="h-5 w-5 text-violet-500 dark:text-violet-300" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
            <line x1="16" y1="2" x2="16" y2="6" />
            <line x1="8" y1="2" x2="8" y2="6" />
            <line x1="3" y1="10" x2="21" y2="10" />
          </svg>
        </div>
      </div>
      <div className="mt-4 flex items-baseline">
        <div className="text-3xl font-extrabold text-violet-700 dark:text-violet-100">{alertsIn7D}</div>
        <div className="ml-2 text-xs font-semibold text-violet-500 dark:text-violet-300">alerts</div>
      </div>
    </div>
    <div className="h-1 w-full bg-gradient-to-r from-violet-200 to-purple-300 dark:from-violet-500/60 dark:to-purple-400/60"></div>
  </div>

  {/* 卡片4 - 7天內損失 */}
  <div className="bg-gradient-to-br from-purple-50 to-violet-100 dark:from-purple-800/80 dark:to-violet-700/80 rounded-xl shadow-lg overflow-hidden border border-purple-200 dark:border-purple-500/30 transition-all duration-300 hover:shadow-purple-400/10 hover:scale-105">
    <div className="p-5">
      <div className="flex items-center justify-between">
        <div className="text-purple-600 dark:text-purple-300 text-sm font-medium tracking-wider uppercase">{t("lossIn7D")}</div>
        <div className="bg-purple-100/70 dark:bg-purple-500/10 p-2 rounded-full">
          <svg className="h-5 w-5 text-purple-500 dark:text-purple-300" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M12 2.69l5.66 5.66a8 8 0 1 1-11.31 0z" />
          </svg>
        </div>
      </div>
      <div className="mt-4 flex items-baseline">
        <div className="text-3xl font-extrabold text-purple-700 dark:text-purple-100">{lossIn7D}</div>
        <div className="ml-2 text-xs font-semibold text-purple-500 dark:text-purple-300">USD</div>
      </div>
    </div>
    <div className="h-1 w-full bg-gradient-to-r from-purple-200 to-violet-300 dark:from-purple-500/60 dark:to-violet-400/60"></div>
  </div>
</div>

 {/* 搜尋框與時間篩選按鈕 */}
<div className="flex flex-col sm:flex-row items-start sm:items-center justify-between mb-6 space-y-4 sm:space-y-0">
  {/* 搜尋框 */}
  <div className="relative w-full sm:w-1/3">
    <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
      <svg className="w-4 h-4 text-purple-500/70 dark:text-purple-400/70" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="11" cy="11" r="8"></circle>
        <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
      </svg>
    </div>
    <input
      type="text"
      placeholder={t("searchPlaceholder")}
      value={searchQuery}
      onChange={(e) => setSearchQuery(e.target.value)}
      className="w-full pl-10 pr-4 py-2.5 bg-white/80 dark:bg-gray-800/80 border border-purple-200 dark:border-purple-700/30 rounded-lg focus:ring-2 focus:ring-purple-300 dark:focus:ring-purple-600/50 focus:border-purple-300 dark:focus:border-purple-700 focus:outline-none transition-all duration-200 text-gray-700 dark:text-gray-200 placeholder-gray-400 dark:placeholder-gray-500"
    />
  </div>
  
  {/* 時間篩選按鈕 */}
  <div className="flex space-x-2">
    <button
      onClick={() => setTimeFilter("1D")}
      className={`px-4 py-2 rounded-lg border transition-all duration-200 font-medium text-sm ${
        timeFilter === "1D"
          ? "bg-gradient-to-r from-purple-600/90 to-indigo-600/90 dark:from-purple-700 dark:to-indigo-700 text-white border-purple-500 dark:border-purple-600/50 shadow-md"
          : "bg-white/80 dark:bg-gray-800/80 hover:bg-purple-50 dark:hover:bg-purple-900/30 text-gray-700 dark:text-gray-300 border-purple-200 dark:border-purple-700/30"
      }`}
    >
      {t("filter1D")}
    </button>
    <button
      onClick={() => setTimeFilter("7D")}
      className={`px-4 py-2 rounded-lg border transition-all duration-200 font-medium text-sm ${
        timeFilter === "7D"
          ? "bg-gradient-to-r from-purple-600/90 to-indigo-600/90 dark:from-purple-700 dark:to-indigo-700 text-white border-purple-500 dark:border-purple-600/50 shadow-md"
          : "bg-white/80 dark:bg-gray-800/80 hover:bg-purple-50 dark:hover:bg-purple-900/30 text-gray-700 dark:text-gray-300 border-purple-200 dark:border-purple-700/30"
      }`}
    >
      {t("filter7D")}
    </button>
    <button
      onClick={() => setTimeFilter("30D")}
      className={`px-4 py-2 rounded-lg border transition-all duration-200 font-medium text-sm ${
        timeFilter === "30D"
          ? "bg-gradient-to-r from-purple-600/90 to-indigo-600/90 dark:from-purple-700 dark:to-indigo-700 text-white border-purple-500 dark:border-purple-600/50 shadow-md"
          : "bg-white/80 dark:bg-gray-800/80 hover:bg-purple-50 dark:hover:bg-purple-900/30 text-gray-700 dark:text-gray-300 border-purple-200 dark:border-purple-700/30"
      }`}
    >
      {t("filter30D")}
    </button>
    <button
      onClick={() => setTimeFilter("all")}
      className={`px-4 py-2 rounded-lg border transition-all duration-200 font-medium text-sm ${
        timeFilter === "all"
          ? "bg-gradient-to-r from-purple-600/90 to-indigo-600/90 dark:from-purple-700 dark:to-indigo-700 text-white border-purple-500 dark:border-purple-600/50 shadow-md"
          : "bg-white/80 dark:bg-gray-800/80 hover:bg-purple-50 dark:hover:bg-purple-900/30 text-gray-700 dark:text-gray-300 border-purple-200 dark:border-purple-700/30"
      }`}
    >
      {t("filterAll")}
    </button>
  </div>
</div>

{/* 報告資料表 */}
<div className="overflow-x-auto rounded-xl shadow-md border border-purple-200/80 dark:border-purple-700/30">
  <table className="min-w-full bg-white/90 dark:bg-gray-800/90">
    <thead>
      <tr className="bg-purple-50 dark:bg-purple-900/30 border-b border-purple-200 dark:border-purple-700/50">
        <th
          className="px-6 py-3.5 text-left text-xs font-semibold text-purple-700 dark:text-purple-300 uppercase tracking-wider cursor-pointer transition-colors hover:bg-purple-100/70 dark:hover:bg-purple-800/30 rounded-tl-xl"
          onClick={() => handleSort("date")}
        >
          <div className="flex items-center space-x-1">
            <span>{t("age")}</span>
            <svg className="w-4 h-4" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M7 10l5 5 5-5" />
            </svg>
          </div>
        </th>
        <th className="px-6 py-3.5 text-left text-xs font-semibold text-purple-700 dark:text-purple-300 uppercase tracking-wider">
          {t("phishingType")}
        </th>
        <th className="px-6 py-3.5 text-left text-xs font-semibold text-purple-700 dark:text-purple-300 uppercase tracking-wider">
          {t("scammer")}
        </th>
        <th className="px-6 py-3.5 text-left text-xs font-semibold text-purple-700 dark:text-purple-300 uppercase tracking-wider">
          {t("phishingUrl")}
        </th>
        <th
          className="px-6 py-3.5 text-left text-xs font-semibold text-purple-700 dark:text-purple-300 uppercase tracking-wider cursor-pointer transition-colors hover:bg-purple-100/70 dark:hover:bg-purple-800/30"
          onClick={() => handleSort("amount")}
        >
          <div className="flex items-center space-x-1">
            <span>{t("lossAmount")}</span>
            <svg className="w-4 h-4" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M7 10l5 5 5-5" />
            </svg>
          </div>
        </th>
        <th className="px-6 py-3.5 text-left text-xs font-semibold text-purple-700 dark:text-purple-300 uppercase tracking-wider">
          {t("lossCurrency")}
        </th>
        <th className="px-6 py-3.5 text-left text-xs font-semibold text-purple-700 dark:text-purple-300 uppercase tracking-wider rounded-tr-xl">
          {t("detail")}
        </th>
      </tr>
    </thead>
    <tbody className="divide-y divide-purple-100 dark:divide-purple-700/30">
      {filteredReports.map((item, index) => (
        <tr
          key={index}
          className="hover:bg-purple-50/70 dark:hover:bg-purple-900/10 transition-colors"
        >
          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700 dark:text-gray-300 font-medium">
            {item.date && !isNaN(new Date(item.date)) 
              ? new Date(item.date).toLocaleDateString('zh-TW', {
                  year: 'numeric',
                  month: '2-digit',
                  day: '2-digit',
                  hour: '2-digit',
                  minute: '2-digit'
                })
              : t("noDate")}
          </td>
          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700 dark:text-gray-300">
            <span className="px-2.5 py-1 rounded-full text-xs font-medium bg-purple-100 dark:bg-purple-900/50 text-purple-800 dark:text-purple-300">
              {t(item.incidentType || "unknown")}
            </span>
          </td>
          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600 dark:text-gray-400 font-mono">
            <div className="flex items-center">
              <span className="truncate max-w-[120px]">{item.scammerWalletAddress || t("noWalletInfo")}</span>
              {item.scammerWalletAddress && (
                <button className="ml-1 text-purple-500 hover:text-purple-700 dark:hover:text-purple-300">
                  <svg className="w-4 h-4" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <rect x="9" y="9" width="13" height="13" rx="2" ry="2"></rect>
                    <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"></path>
                  </svg>
                </button>
              )}
            </div>
          </td>
          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600 dark:text-gray-400">
            <div className="flex items-center">
              <span className="truncate max-w-[120px]">
                {item.websites && item.websites.length > 0 ? item.websites[0].url : t("noWebsiteInfo")}
              </span>
              {item.websites && item.websites.length > 0 && (
                <a 
                  href={item.websites[0].url} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="ml-1 text-purple-500 hover:text-purple-700 dark:hover:text-purple-300"
                >
                  <svg className="w-4 h-4" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <rect x="9" y="9" width="13" height="13" rx="2" ry="2"></rect>
                    <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"></path>
                  </svg>
                </a>
              )}
            </div>
          </td>
          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700 dark:text-gray-300 font-semibold">
            {item.amount ? parseFloat(item.amount).toFixed(1) : "0.0"}
          </td>
          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700 dark:text-gray-300">
            <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-indigo-100 dark:bg-indigo-900/50 text-indigo-800 dark:text-indigo-300">
              {item.currency || t("noTokenInfo")}
            </span>
          </td>
          <td className="px-6 py-4 whitespace-nowrap text-sm">
            <Link
              to={`/public-report-details/${item.originalId}`}
              className="px-3 py-1.5 inline-flex items-center rounded-lg bg-purple-100 dark:bg-purple-800/30 text-purple-600 dark:text-purple-300 hover:bg-purple-200 dark:hover:bg-purple-700/40 font-medium text-xs transition-colors"
            >
              <svg className="w-3.5 h-3.5 mr-1" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"></path>
                <polyline points="15 3 21 3 21 9"></polyline>
                <line x1="10" y1="14" x2="21" y2="3"></line>
              </svg>
              {t("detail")}
            </Link>
          </td>
        </tr>
      ))}
      {filteredReports.length === 0 && (
        <tr>
          <td className="px-6 py-10 text-center text-purple-500 dark:text-purple-400 font-medium text-sm" colSpan="7">
            <div className="flex flex-col items-center justify-center">
              <svg className="w-10 h-10 mb-2 opacity-70" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="10"></circle>
                <line x1="12" y1="8" x2="12" y2="12"></line>
                <line x1="12" y1="16" x2="12.01" y2="16"></line>
              </svg>
              {t("noData")}
            </div>
          </td>
        </tr>
      )}
    </tbody>
  </table>
</div>

    </div>
  );
};

export default ReportList;
